import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Typography,
  MenuItem,
  FormControlLabel,
  FormHelperText,
  Alert,
  CircularProgress,
} from '@mui/material';
import ImageSelector from './ImageSelector';
import BadgeCriteriaSelector from './BadgeCriteriaSelector';
import TextField from '../UIKit/TextField';
import Select from '../UIKit/Select';
import Button from '../UIKit/Button';
import Switch from '../UIKit/Switch';
import { useTranslation } from 'react-i18next';

const BadgeFormDialog = ({
  open,
  onClose,
  title,
  badge,
  formErrors,
  submitLoading,
  imagePreview,
  setImagePreview,
  imageFile,
  setImageFile,
  imageUploadTab,
  setImageUploadTab,
  badgeCriteria,
  advancedCriteriaMode,
  handleInputChange,
  handleCategoryChange,
  handleToggleAdvancedMode,
  handleOperatorChange,
  handleCriteriaChange,
  handleCriteriaTypeChange,
  handleAdvancedCriteriaParamChange,
  handleAddCondition,
  handleRemoveCondition,
  handleSave,
  availableCourses,
  availableAchievements,
  availableTests,
  availableChallenges,
  loadingOptions,
  getRarityColor,
}) => {
  const { t } = useTranslation(['badges', 'common']);
  const isEdit = !!badge._id;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: '16px',
        },
      }}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              name="name"
              label={t('badges:forms.name')}
              fullWidth
              value={badge.name}
              onChange={handleInputChange}
              error={!!formErrors.name}
              helperText={formErrors.name}
              required
              sx={{ mb: 2 }}
            />

            <TextField
              name="description"
              label={t('badges:forms.description')}
              fullWidth
              multiline
              rows={4}
              value={badge.description}
              onChange={handleInputChange}
              error={!!formErrors.description}
              helperText={formErrors.description}
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <ImageSelector
              isEdit={isEdit}
              badge={badge}
              handleChange={handleInputChange}
              imagePreview={imagePreview}
              setImagePreview={setImagePreview}
              imageFile={imageFile}
              setImageFile={setImageFile}
              imageUploadTab={imageUploadTab}
              setImageUploadTab={setImageUploadTab}
              formErrors={formErrors}
              setFormErrors={() => {}}
              getRarityColor={getRarityColor}
            />
          </Grid>

          <Grid item xs={12} sx={{ mt: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              {t('badges:forms.generalInfo')}
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Select
                  fullWidth
                  name="category"
                  value={badge.category}
                  onChange={(e) => handleCategoryChange(e, isEdit)}
                  label={t('badges:forms.category')}
                >
                  <MenuItem value="course">
                    {t('badges:category.course')}
                  </MenuItem>
                  <MenuItem value="achievement">
                    {t('badges:category.achievement')}
                  </MenuItem>
                  <MenuItem value="community">
                    {t('badges:category.community')}
                  </MenuItem>
                  <MenuItem value="special">
                    {t('badges:category.special')}
                  </MenuItem>
                  <MenuItem value="seasonal">
                    {t('badges:category.seasonal')}
                  </MenuItem>
                  <MenuItem value="challenge">
                    {t('badges:category.challenge')}
                  </MenuItem>
                  <MenuItem value="streak">
                    {t('badges:category.streak')}
                  </MenuItem>
                  <MenuItem value="skill">{t('badges:category.skill')}</MenuItem>
                </Select>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Select
                  name="rarity"
                  value={badge.rarity}
                  onChange={handleInputChange}
                  label={t('badges:forms.rarity')}
                  fullWidth
                >
                  <MenuItem value="common">{t('badges:rarity.common')}</MenuItem>
                  <MenuItem value="uncommon">
                    {t('badges:rarity.uncommon')}
                  </MenuItem>
                  <MenuItem value="rare">{t('badges:rarity.rare')}</MenuItem>
                  <MenuItem value="epic">{t('badges:rarity.epic')}</MenuItem>
                  <MenuItem value="legendary">
                    {t('badges:rarity.legendary')}
                  </MenuItem>
                </Select>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Select
                  name="tier"
                  value={badge.tier}
                  onChange={handleInputChange}
                  label={t('badges:forms.tier')}
                  fullWidth
                >
                  <MenuItem value="bronze">{t('badges:tier.bronze')}</MenuItem>
                  <MenuItem value="silver">{t('badges:tier.silver')}</MenuItem>
                  <MenuItem value="gold">{t('badges:tier.gold')}</MenuItem>
                  <MenuItem value="platinum">
                    {t('badges:tier.platinum')}
                  </MenuItem>
                </Select>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  name="isSecret"
                  checked={badge.isSecret}
                  onChange={handleInputChange}
                  sx={{ mx: 1 }}
                />
              }
              label={t('badges:forms.isSecret')}
            />
            <FormHelperText>
              {t('badges:forms.isSecretHelper')}
            </FormHelperText>
          </Grid>

          <Grid item xs={12} sx={{ mt: 1 }}>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              {t('badges:forms.criteriaSettings')}
            </Typography>

            {formErrors.criteria && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {formErrors.criteria}
              </Alert>
            )}

            <BadgeCriteriaSelector
              category={badge.category}
              badgeCriteria={badgeCriteria}
              advancedCriteriaMode={advancedCriteriaMode}
              handleToggleAdvancedMode={handleToggleAdvancedMode}
              handleOperatorChange={handleOperatorChange}
              handleCriteriaChange={handleCriteriaChange}
              handleCriteriaTypeChange={handleCriteriaTypeChange}
              handleAdvancedCriteriaParamChange={
                handleAdvancedCriteriaParamChange
              }
              handleAddCondition={handleAddCondition}
              handleRemoveCondition={handleRemoveCondition}
              availableCourses={availableCourses}
              availableAchievements={availableAchievements}
              availableTests={availableTests}
              availableChallenges={availableChallenges}
              loadingOptions={loadingOptions}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose} disabled={submitLoading}>
          {t('common:actions.cancel')}
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={submitLoading}
        >
          {submitLoading ? (
            <CircularProgress size={24} />
          ) : isEdit ? (
            t('common:actions.save')
          ) : (
            t('common:actions.create')
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BadgeFormDialog;
